/**
 * useSignalRManager is a custom React hook that wraps around the SignalRManager class
 * to provide a reusable and centralized way to manage SignalR connections across your app.
 *
 * :white_check_mark: Features:
 * - Initializes a SignalR connection using a dynamic URL and token
 * - Automatically listens for "connect" and "disconnect" events
 * - Updates local React state to reflect connection status
 * - Provides access to SignalRManager methods for sending updates, subscribing to categories, etc.
 * - Cleans up the connection when the component unmounts
 *
 * :wrench: Parameters:
 * @param url - The full SignalR hub URL (e.g. https://domain/hubs/accounting)
 * @param token - The JWT token used for authenticating the connection
 * @param onConnect - Optional callback triggered when the connection is established
 * @param onDisconnect - Optional callback triggered when the connection is closed
 * @param onError - Optional callback triggered when connection fails or errors occur
 *
 * :package: Returns:
 * - connected: boolean indicating current connection status
 * - sendUpdate: method to send categorized updates to the hub
 * - sendPrivateUpdate: method to send private updates to a specific user
 * - subscribeToCategory: method to subscribe to a category stream
 * - unsubscribeFromCategory: method to unsubscribe from a category stream
 * - on: method to register custom event listeners
 * - off: method to remove custom event listeners
 *
 * :bulb: Usage:
 * Call this hook inside any component to connect to a SignalR hub dynamically.
 * It ensures consistent connection lifecycle management and event handling.
 */

/***
 * Example Usage:
 ***
 import React from "react";
   import { useSignalRManager } from "./useSignalRManager";

const AccountingPage = () => {
  const accessToken = localStorage.getItem("access_token") || "";

  const { connected, subscribeToCategory, on } = useSignalRManager({
    url: "https://accountingbackend-ghguc9gth7h8ebek.eastus-01.azurewebsites.net/hubs/accounting",
    token: accessToken,
    onConnect: () => console.log("Connected to Accounting Hub"),
    onDisconnect: () => console.log("Disconnected from Accounting Hub")
  });

  React.useEffect(() => {
    subscribeToCategory("RoomBooking");

    on("RoomBooking", (data) => {
      console.log("📦 Room booking update:", data);
    });
  }, []);

  return <div>{connected ? "Connected to Accounting Hub" : "Connecting..."}</div>;
};

export default AccountingPage;
 * 
 * 
 */

// UseSignalRConnection (updated)
import { useCallback, useEffect, useState } from "react";
import signalRManager from "./SignalRManager";

type EventHandler = (...args: any[]) => void;

interface UseSignalRConnectionOptions {
  url: string;
  token: string;
  onConnect?: () => void;
  onDisconnect?: () => void;
  hubHandlers?: Record<string, EventHandler>; // ✅ Added support for custom hub events
  wildcardHandler?: (methodName: string, payload: any) => void; // ✅ Optional wildcard handler
}

export const useSignalRConnection = ({
  url,
  token,
  onConnect,
  onDisconnect,
  hubHandlers,
  wildcardHandler,
}: UseSignalRConnectionOptions) => {
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!url || !token) return;

    const rawConnection = signalRManager.getConnection(url);

    // ✅ Register hubHandlers BEFORE initialize/start
    if (hubHandlers && rawConnection) {
      Object.entries(hubHandlers).forEach(([event, handler]) => {
        rawConnection.on(event, handler);
        console.log(`[SignalR] Registered handler for ${event}`);
      });
    }

    // ✅ Register wildcard handler BEFORE start
    if (wildcardHandler && rawConnection) {
      rawConnection.on("*", wildcardHandler);
      console.log(`[SignalR] Registered wildcard handler`);
    }

    // Initialize connection
    signalRManager.initialize({ url, token });

    const handleConnect = () => {
      setConnected(true);
      onConnect?.();
    };

    const handleDisconnect = () => {
      setConnected(false);
      onDisconnect?.();
    };

    signalRManager.on(url, "connect", handleConnect);
    signalRManager.on(url, "disconnect", handleDisconnect);

    return () => {
      signalRManager.off(url, "connect", handleConnect);
      signalRManager.off(url, "disconnect", handleDisconnect);

      // Clean up hubHandlers
      if (hubHandlers && rawConnection) {
        Object.entries(hubHandlers).forEach(([event, handler]) => {
          rawConnection.off(event, handler);
          console.log(`[SignalR] Unregistered handler for ${event}`);
        });
      }

      // Clean up wildcard
      if (wildcardHandler && rawConnection) {
        rawConnection.off("*");
        console.log(`[SignalR] Unregistered wildcard handler`);
      }

      setConnected(false);
    };
  }, [url, token, onConnect, onDisconnect, hubHandlers, wildcardHandler]);

  const connection = signalRManager.getConnection(url);

  const on = useCallback(
    (event: string, handler: EventHandler) => {
      signalRManager.on(url, event, handler);
    },
    [url]
  );

  const off = useCallback(
    (event: string, handler: EventHandler) => {
      signalRManager.off(url, event, handler);
    },
    [url]
  );

  const sendUpdate = useCallback(
    (category: string, payload: any) => {
      signalRManager.sendUpdate(url, category, payload);
    },
    [url]
  );

  const sendPrivateUpdate = useCallback(
    (userId: string, payload: any) => {
      signalRManager.sendPrivateUpdate(url, userId, payload);
    },
    [url]
  );

  return {
    connected,
    connection,
    on,
    off,
    sendUpdate,
    sendPrivateUpdate,
  };
};